package diana.soleil.design.iraniancanadienbazzar.core.model

/**
 * A structured business fact ("attribute") an owner can switch on — the Google-Business-Profile
 * pattern: the DETAIL a listing offers depends on its category (a restaurant declares halal/
 * dine-in/delivery; a clinic declares accepting-new-patients; a realtor declares virtual tours),
 * and structured facts beat free-text descriptions for search/filtering later. Vocabulary drawn
 * from schema.org LocalBusiness subtypes. See docs/DATA_MODEL.md § businesses.attributes.
 *
 * v1 attributes are all boolean toggles stored as a list of enabled [key]s on the business doc.
 * Labels are author-facing CONTENT (like metro names), so they localize via [LocalizedText] here,
 * not app string resources.
 */
data class AttributeDef(
    val key: String,
    val label: LocalizedText,
)

/**
 * The category-scoped attribute catalog. [forCategory] = the common set every business gets plus
 * the vertical's own facts. Unknown/legacy keys on a doc are simply not rendered, and categories
 * without an entry still get [common] — adding a vertical is one list here, zero schema changes.
 */
object BusinessAttributeRegistry {

    private fun def(key: String, en: String, fr: String, fa: String) =
        AttributeDef(key, LocalizedText(en = en, fr = fr, fa = fa))

    val common: List<AttributeDef> = listOf(
        def("wheelchair_accessible", "Wheelchair accessible", "Accessible en fauteuil roulant", "دسترسی برای ویلچر"),
        def("free_parking", "Free parking", "Stationnement gratuit", "پارکینگ رایگان"),
        def("card_payments", "Card payments", "Paiement par carte", "پرداخت با کارت"),
        def("online_appointments", "Online appointments", "Rendez-vous en ligne", "نوبت‌گیری آنلاین"),
    )

    private val byCategory: Map<String, List<AttributeDef>> = mapOf(
        "food_restaurants" to listOf(
            def("halal", "Halal", "Halal", "حلال"),
            def("dine_in", "Dine-in", "Sur place", "سرو در محل"),
            def("takeout", "Takeout", "À emporter", "بیرون‌بر"),
            def("delivery", "Delivery", "Livraison", "ارسال"),
            def("outdoor_seating", "Outdoor seating", "Terrasse", "فضای باز"),
            def("reservations", "Takes reservations", "Réservations acceptées", "رزرو میز"),
            def("vegetarian_options", "Vegetarian options", "Options végétariennes", "گزینه گیاهی"),
            def("catering", "Catering", "Service traiteur", "کترینگ"),
        ),
        "grocery_specialty_food" to listOf(
            def("halal", "Halal", "Halal", "حلال"),
            def("delivery", "Delivery", "Livraison", "ارسال"),
            def("fresh_bread_daily", "Fresh bread daily", "Pain frais chaque jour", "نان تازه روزانه"),
            def("bulk_orders", "Bulk orders", "Commandes en gros", "سفارش عمده"),
        ),
        "medical_health" to listOf(
            def("accepting_new_patients", "Accepting new patients", "Accepte de nouveaux patients", "پذیرش بیمار جدید"),
            def("direct_billing", "Direct insurance billing", "Facturation directe aux assurances", "تسویه مستقیم با بیمه"),
            def("walk_ins", "Walk-ins welcome", "Sans rendez-vous", "پذیرش بدون نوبت"),
            def("telehealth", "Virtual appointments", "Consultations virtuelles", "ویزیت آنلاین"),
        ),
        "beauty_wellness" to listOf(
            def("walk_ins", "Walk-ins welcome", "Sans rendez-vous", "پذیرش بدون نوبت"),
            def("home_service", "Home service", "Service à domicile", "خدمات در منزل"),
            def("bridal_packages", "Bridal packages", "Forfaits mariage", "پکیج عروس"),
        ),
        "legal_services" to listOf(
            def("free_consultation", "Free consultation", "Consultation gratuite", "مشاوره رایگان"),
            def("virtual_consult", "Virtual consultations", "Consultations virtuelles", "مشاوره آنلاین"),
            def("notary_services", "Notary services", "Services de notariat", "خدمات محضری"),
        ),
        "finance_accounting" to listOf(
            def("free_consultation", "Free consultation", "Consultation gratuite", "مشاوره رایگان"),
            def("virtual_consult", "Virtual consultations", "Consultations virtuelles", "مشاوره آنلاین"),
            def("tax_filing", "Tax filing", "Déclarations de revenus", "تنظیم اظهارنامه مالیاتی"),
            def("newcomer_programs", "Newcomer programs", "Programmes pour nouveaux arrivants", "برنامه‌های تازه‌واردان"),
        ),
        "real_estate" to listOf(
            def("virtual_tours", "Virtual tours", "Visites virtuelles", "بازدید مجازی"),
            def("free_evaluation", "Free home evaluation", "Évaluation gratuite", "ارزیابی رایگان ملک"),
            def("rentals", "Rentals", "Locations", "اجاره"),
            def("commercial", "Commercial properties", "Immobilier commercial", "املاک تجاری"),
            def("newcomer_financing_help", "Newcomer financing help", "Aide au financement pour nouveaux arrivants", "راهنمایی وام تازه‌واردان"),
        ),
        "education_tutors" to listOf(
            def("online_classes", "Online classes", "Cours en ligne", "کلاس آنلاین"),
            def("in_person_classes", "In-person classes", "Cours en personne", "کلاس حضوری"),
            def("kids_programs", "Kids programs", "Programmes pour enfants", "برنامه کودکان"),
            def("adult_programs", "Adult programs", "Programmes pour adultes", "برنامه بزرگسالان"),
        ),
        "auto_services" to listOf(
            def("loaner_car", "Loaner car", "Voiture de courtoisie", "خودروی جایگزین"),
            def("towing", "Towing", "Remorquage", "یدک‌کش"),
            def("certified_mechanic", "Certified mechanic", "Mécanicien certifié", "مکانیک دارای گواهی"),
        ),
        "home_services" to listOf(
            def("free_estimates", "Free estimates", "Estimations gratuites", "برآورد رایگان"),
            def("licensed_insured", "Licensed & insured", "Licencié et assuré", "دارای مجوز و بیمه"),
            def("emergency_service", "24/7 emergency service", "Service d'urgence 24/7", "خدمات اضطراری ۲۴ ساعته"),
        ),
        "shopping" to listOf(
            def("online_orders", "Online orders", "Commandes en ligne", "سفارش آنلاین"),
            def("delivery", "Delivery", "Livraison", "ارسال"),
        ),
    )

    /**
     * Cross-category facts surfaced as SEARCH FILTERS ("halal near me"). Curated: high-intent keys
     * that exist in at least one category catalog above.
     */
    val popularFilters: List<AttributeDef> = listOf(
        def("halal", "Halal", "Halal", "حلال"),
        def("wheelchair_accessible", "Wheelchair accessible", "Accessible en fauteuil roulant", "دسترسی برای ویلچر"),
        def("delivery", "Delivery", "Livraison", "ارسال"),
        def("online_appointments", "Online appointments", "Rendez-vous en ligne", "نوبت‌گیری آنلاین"),
        def("free_consultation", "Free consultation", "Consultation gratuite", "مشاوره رایگان"),
        def("accepting_new_patients", "Accepting new patients", "Accepte de nouveaux patients", "پذیرش بیمار جدید"),
        def("walk_ins", "Walk-ins welcome", "Sans rendez-vous", "پذیرش بدون نوبت"),
    )

    /** Every attribute key any category can declare — used to prune keys a new category doesn't allow. */
    val allKeys: Set<String> get() = (common + byCategory.values.flatten()).map { it.key }.toSet()

    /**
     * [keys] minus anything [categoryId] can't declare. Changing a listing's category must drop the old
     * category's answers ("Towing" on a law firm), or they linger in the data and resurface if the
     * category is ever changed back.
     */
    fun keepValidFor(categoryId: String?, keys: Collection<String>): Set<String> {
        val allowed = forCategory(categoryId).map { it.key }.toSet()
        return keys.filter { it in allowed }.toSet()
    }

    /** The attribute set a business of [categoryId] can declare (common + vertical). */
    fun forCategory(categoryId: String?): List<AttributeDef> =
        common + byCategory[categoryId].orEmpty()

    /** Resolves the enabled [keys] of a [categoryId] business to renderable defs, unknown keys dropped. */
    fun resolve(categoryId: String?, keys: Collection<String>): List<AttributeDef> {
        val defs = forCategory(categoryId).associateBy { it.key }
        return keys.mapNotNull { defs[it] }
    }
}
